/**
 * Hover/focus tooltip rendered in a fixed-position portal (reliable vs native `title`).
 */

import { createPortal } from 'react-dom';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface FloatingTooltipProps {
  /** Text shown in the floating panel. */
  tooltip: string;
  /** Full accessible name for the trigger (include short visible label + explanation). */
  ariaLabel: string;
  children: ReactNode;
  wrapClassName?: string;
  triggerClassName?: string;
  /** "above" avoids covering the first data row when used on table headers. */
  placement?: 'above' | 'below';
}

type Layout = { top: number; left: number; maxW: number; placement: 'above' | 'below' };

export function FloatingTooltip({
  tooltip,
  ariaLabel,
  children,
  wrapClassName,
  triggerClassName,
  placement = 'below',
}: FloatingTooltipProps): JSX.Element {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [visible, setVisible] = useState(false);
  const [layout, setLayout] = useState<Layout>({
    top: 0,
    left: 0,
    maxW: 320,
    placement: 'below',
  });

  const clearHideTimer = (): void => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const updateLayout = useCallback(
    (pos: 'above' | 'below'): void => {
      const el = triggerRef.current;
      if (el === null) {
        return;
      }
      const r = el.getBoundingClientRect();
      const margin = 8;
      const maxW = Math.min(32 * 16, window.innerWidth - 2 * margin);
      let left = r.left;
      if (left + maxW > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - margin - maxW);
      }
      const top = pos === 'above' ? r.top - margin : r.bottom + margin;
      setLayout({ top, left, maxW, placement: pos });
    },
    [],
  );

  const scheduleHide = (): void => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => setVisible(false), 120);
  };

  useEffect(
    () => (): void => {
      clearHideTimer();
    },
    [],
  );

  const show = (): void => {
    clearHideTimer();
    updateLayout(placement);
    setVisible(true);
  };

  return (
    <span className={wrapClassName}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-label={ariaLabel}
        aria-describedby={visible ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
      >
        {children}
      </button>
      {visible &&
        createPortal(
          <span
            id={tooltipId}
            role="tooltip"
            className="floating-tooltip-panel"
            style={{
              position: 'fixed',
              top: layout.top,
              left: layout.left,
              maxWidth: layout.maxW,
              zIndex: 10000,
              transform: layout.placement === 'above' ? 'translateY(-100%)' : undefined,
            }}
            onMouseEnter={() => {
              clearHideTimer();
              setVisible(true);
            }}
            onMouseLeave={scheduleHide}
          >
            {tooltip}
          </span>,
          document.body,
        )}
    </span>
  );
}
